import type {
    BaseLogger,
    CoreSchemaConfig,
    RefInfo,
    ResolvedReference,
    SchemaFile,
    WixReferenceAnalysis
} from '@wix/document-services-types'
import _ from 'lodash'
import draftSchema from './draftSchema.json'
import {CannotFindSchemaError, SchemaValidationError, SchemaValidationErrorDetails} from './errors'
import {combineAllOf, dereference} from './schemaUtils'
import {buildVariableReferencePropertiesForSingleSchema} from '../../common/variables/variableReferencePropertiesBuilder'
import {buildVariableConnectionsForSchema} from '../../common/variables/variableConnectionsBuilder'
import * as variablesManifest from '../namespaces/variables/variablesManifest.json'
import * as referenceUtil from './referenceUtil'
import {createInstance, Ajv6Type, ErrorObject6, Ajv8Type} from '../utils/ajv'
import {ReportableError} from '@wix/document-manager-utils'
import {convertToUnevaluatedForRemovalInstance, convertToSchemasWithDiscriminators} from '../utils/convertTo2019'
import {removeUnevaluated} from '../utils/removeUnevaluated'

export type ValidationFunction = (namespace: string, dataTypeName: string, data: any) => void

const convertAjvError = (error: ErrorObject6): SchemaValidationErrorDetails => {
    return _.pick(error, ['message', 'dataPath', 'keyword', 'schemaPath', 'params']) as SchemaValidationErrorDetails
}

const convertAjvErrors = (errors: ErrorObject6[]): SchemaValidationError =>
    new SchemaValidationError(_.map(errors, convertAjvError))

interface CoreValidators {
    withDefaults: Ajv6Type | Ajv8Type
    validation: Ajv6Type | Ajv8Type
    removing?: Ajv6Type | Ajv8Type
    strict?: Ajv6Type | Ajv8Type
}

const keyByPath = (refInfos: RefInfo[]) => _.keyBy(refInfos, info => info.path.join('.'))

const requiredReferencesFields: (keyof RefInfo)[] = [
    'jsonPointer',
    'isList',
    'referencedMap',
    'shouldCollect',
    'shouldValidate',
    'refTypes',
    'path'
]
const optionalReferencesFields: (keyof RefInfo)[] = ['isRefOwner', 'isRelationalSplit', 'dontSerialize']
const allowedReferencesFields = new Set([...requiredReferencesFields, ...optionalReferencesFields])

const createSchemaCore = (
    coreSchemaConfig: CoreSchemaConfig,
    referencesMap: WixReferenceAnalysis,
    schemaServiceOptions?: Record<string, boolean>,
    logger?: BaseLogger
) => {
    const {namespaces, restrictedSchemas, permanentDataTypes, formats, restrictionMap} = coreSchemaConfig
    const ajvOptions = {strict: false, useDefaults: false, allowUnionTypes: true, strictNumbers: true}
    const validators = {
        withDefaults: createInstance({...ajvOptions, useDefaults: true}, true, formats),
        validation: createInstance(ajvOptions, true, formats)
    } as CoreValidators
    if (restrictionMap) {
        validators.removing = createInstance(
            {
                ...ajvOptions,
                removeAdditional: 'failing',
                allErrors: true,
                discriminator: true
            },
            true,
            formats
        )
        validators.strict = createInstance(ajvOptions, true, formats)
    }
    const validatorsToCompare: Partial<CoreValidators> = {}
    if (schemaServiceOptions?.validateTwiceAndReport) {
        const ajv6Options = {useDefaults: false, extendRefs: true, strictNumbers: true, formats}
        validatorsToCompare.removing = createInstance(
            {
                ...ajv6Options,
                removeAdditional: 'failing',
                allErrors: true,
                discriminator: true
            },
            false,
            formats
        )
        validatorsToCompare.strict = createInstance(ajv6Options, false, formats)
    }

    const cachedInflatedSchemas = new Map<string, any>()

    function addInflatedSchemaToCache(namespace: string, dataType: string, schema: any) {
        const cacheKey = getValidationKey(namespace, dataType)
        cachedInflatedSchemas.set(cacheKey, schema)
    }

    function getInflatedSchemaFromCache(namespace: string, dataType: string) {
        const cacheKey = getValidationKey(namespace, dataType)
        return cachedInflatedSchemas.get(cacheKey)
    }

    function deleteInflatedSchemaFromCache(cacheKey: string) {
        cachedInflatedSchemas.delete(cacheKey)
    }

    function registerSchema(schema: SchemaFile, id: string) {
        _.forOwn(validators, (validator, validatorName) => {
            validator!.addSchema(
                validatorName === 'removing'
                    ? convertToUnevaluatedForRemovalInstance(convertToSchemasWithDiscriminators(_.cloneDeep(schema)))
                    : schema,
                id
            )
        })
        if (schemaServiceOptions?.validateTwiceAndReport) {
            _.forOwn(validatorsToCompare, validator => {
                validator!.addSchema(schema, id)
            })
        }
    }

    function addInitialSchema(schema: SchemaFile, id: string, namespace: string) {
        _.forOwn(validators, (validator, validatorName) => {
            let schemaToRegister =
                (['removing', 'strict'].includes(validatorName) ? restrictionMap![namespace] : schema) ?? schema
            if (validatorName === 'removing') {
                schemaToRegister = convertToUnevaluatedForRemovalInstance(
                    convertToSchemasWithDiscriminators(_.cloneDeep(schemaToRegister))
                )
            }
            validator!.addSchema(schemaToRegister, id)
        })
        if (schemaServiceOptions?.validateTwiceAndReport) {
            _.forOwn(validatorsToCompare, (validator, validatorName) => {
                const schemaToRegister =
                    (['removing', 'strict'].includes(validatorName) ? restrictionMap![namespace] : schema) ?? schema
                validator!.addSchema(schemaToRegister, id)
            })
        }
    }

    _.forOwn(namespaces, (schema: SchemaFile, name: string) => {
        addInitialSchema(schema, schema.$id ?? name, name)
    })

    function removeSchema(id: string) {
        _.forOwn(validators, validator => {
            validator!.removeSchema(id)
        })
        if (schemaServiceOptions?.validateTwiceAndReport) {
            _.forOwn(validatorsToCompare, validator => {
                validator!.removeSchema(id)
            })
        }
        deleteInflatedSchemaFromCache(id)
    }

    function getValidationKey(namespace: string, dataTypeName: string): string {
        const ns = namespaces[namespace]
        return `${ns.$id}#/${dataTypeName}`
    }

    function validateWithCustomValidator(
        validatorsToUse: (Ajv6Type | Ajv8Type)[],
        namespace: string,
        dataTypeName: string,
        data: any,
        validatorsToUseToCompare: (Ajv6Type | Ajv8Type)[],
        duplicateDataForComparison: boolean,
        context: Record<string, any>
    ): void {
        if (!(namespace in namespaces)) {
            throw new CannotFindSchemaError(namespace, dataTypeName)
        }
        let duplicatedData: any
        if (duplicateDataForComparison) {
            duplicatedData = _.cloneDeep(data)
        }
        const key = getValidationKey(namespace, dataTypeName)
        const validationErrors: any = _.reduce<Ajv6Type | Ajv8Type, any[]>(
            validatorsToUse,
            (errors: any[], validator, index) => {
                const validationFunction = validator.getSchema(key)
                const isValid = validationFunction?.(data)
                if (!isValid) {
                    if (context.validator === 'removeAdditionalProperties' && index === 0) {
                        removeUnevaluated(data, validationFunction?.errors as any[])
                        const isValidAfterRemoval = validationFunction?.(data)
                        if (!isValidAfterRemoval) {
                            errors.push(...(validationFunction?.errors ?? []))
                        }
                    } else {
                        errors.push(...(validationFunction?.errors ?? []))
                    }
                }
                return errors
            },
            []
        )
        if (
            schemaServiceOptions?.validateTwiceAndReport &&
            validatorsToUseToCompare.length > 0 &&
            !data?._isDraftItem
        ) {
            const extraValidationErrors: any = _.reduce<Ajv6Type | Ajv8Type, any[]>(
                validatorsToUseToCompare,
                (errors: any[], extraValidator, index) => {
                    const extraValidatorFunction = extraValidator.getSchema(key)
                    const isValid = extraValidatorFunction?.(duplicatedData ?? data)
                    if (!isValid) {
                        errors.push({
                            validator: index,
                            errors: extraValidatorFunction?.errors
                        })
                    }
                    return errors
                },
                []
            )
            if ((validationErrors.length === 0) !== (extraValidationErrors.length === 0)) {
                logger?.captureError(
                    new ReportableError({
                        errorType: 'restrictionMapValidationMismatch',
                        message: 'Validation mismatch between validators',
                        extras: {
                            validationKey: key,
                            data: JSON.stringify(data),
                            validOriginal: validationErrors.length === 0,
                            validCompared: extraValidationErrors.length === 0,
                            validator: context.validator
                        }
                    })
                )
            }
        }
        if (validationErrors?.length > 0) {
            throwValidationErrors(validationErrors)
        }
    }

    // mutate
    const addDefaultsAndValidate: ValidationFunction = (namespace: string, dataTypeName: string, data: any) => {
        validateWithCustomValidator(
            schemaServiceOptions?.useDefaultStrict && validators.strict
                ? [validators.withDefaults, validators.strict]
                : [validators.withDefaults],
            namespace,
            dataTypeName,
            data,
            [],
            false,
            {
                validator: 'addDefaultsAndValidate'
            }
        )
    }

    const validateStrict: ValidationFunction = (namespace: string, dataTypeName: string, data: any) => {
        const dataToValidate = _.cloneDeep(data)
        validateWithCustomValidator(
            [validators.validation, validators.strict ?? validators.withDefaults],
            namespace,
            dataTypeName,
            dataToValidate,
            schemaServiceOptions?.validateTwiceAndReport
                ? [validatorsToCompare!.strict! ?? validators.withDefaults!]
                : [],
            false,
            {
                validator: 'validateStrict'
            }
        )
    }

    const validateNoDefaults: ValidationFunction = (namespace: string, dataTypeName: string, data: any) => {
        validateWithCustomValidator([validators.validation], namespace, dataTypeName, data, [], false, {
            validator: 'validateNoDefaults'
        })
    }

    const validateReferences = (refs: unknown): refs is RefInfo[] => {
        if (!Array.isArray(refs)) {
            throw new Error('Expected an array')
        }

        refs.forEach((ref, index) => {
            if (typeof ref !== 'object' || ref === null) {
                throw new Error(`Item at index ${index} is not an object`)
            }
            const keys = Object.keys(ref)
            for (const field of requiredReferencesFields) {
                if (!(field in ref)) {
                    throw new Error(`Missing required field "${field}" at index ${index}`)
                }
            }
            for (const key of keys) {
                if (!allowedReferencesFields.has(key as keyof RefInfo)) {
                    throw new Error(`Unexpected field "${key}" at index ${index}`)
                }
            }
        })

        return true
    }

    function removeAdditionalProperties(namespace: string, dataTypeName: string, data: any): void {
        if (validators.removing) {
            validateWithCustomValidator(
                [validators.removing, validators.validation],
                namespace,
                dataTypeName,
                data,
                schemaServiceOptions?.validateTwiceAndReport ? [validatorsToCompare!.removing!] : [],
                true,
                {
                    validator: 'removeAdditionalProperties'
                }
            )
        }
    }

    function throwValidationErrors(errors?: ErrorObject6[] | null) {
        throw convertAjvErrors(errors ?? [])
    }

    function hasNamespace(namespace: string) {
        return !!_.get(namespaces, [namespace])
    }

    function hasSchemaForDataType(namespace: string, dataTypeName: string) {
        return !!_.get(namespaces, [namespace, dataTypeName])
    }

    // This is a workaround to remove internal cache created by AJV fragments behaviour
    // We get to this flow only in "Schema Dev Mode"
    // See - https://github.com/ajv-validator/ajv/issues/1293
    function clearAjvFragmentCacheForSchema(namespace: string, schema: SchemaFile, schemaName: string) {
        const key = getValidationKey(namespace, schemaName)
        removeSchema(`#/${schemaName}`)
        removeSchema(key)
        registerSchema(schema, key)
        registerSchema(schema, `#/${schemaName}`)
    }

    function extendDraftSchema(schema: any) {
        if (schema.isDraftSchema) {
            if (schema.allOf) {
                return {
                    isDraftSchema: true,
                    ...schema,
                    allOf: [draftSchema, ...schema.allOf]
                }
            }

            if (schema.properties) {
                return {
                    ..._.omit(schema, ['properties']),
                    isDraftSchema: true,
                    properties: _.merge(schema.properties, draftSchema.properties),
                    required: _.union(schema.required || [], draftSchema.required)
                }
            }
            return {
                ...schema,
                isDraftSchema: true,
                properties: draftSchema.properties,
                required: _.union(schema.required || [], draftSchema.required)
            }
        }
        return schema
    }

    const getVariableOptions = (key: string) => {
        let [nsName, schemaName] = key.split('#/')
        if (!schemaName) {
            schemaName = nsName
            nsName = 'commonSchemas'
        }
        const shortSchemaName = schemaName.split('/').pop() ?? ''
        const nsNameInVariablesManifest = nsName.replace('.json', '').replace('urn:dm:schemas:', '')
        return variablesManifest.variables[nsNameInVariablesManifest]?.[shortSchemaName]
    }
    const extendWithVariables = (schema: any) => {
        const variableConnectionsMap = buildVariableReferencePropertiesForSingleSchema({}, schema, getVariableOptions)
        if (_.isEmpty(variableConnectionsMap)) {
            return schema
        }
        const variableConnectionsSchema = buildVariableConnectionsForSchema(variableConnectionsMap)
        return {
            type: 'object',
            allOf: [schema, variableConnectionsSchema]
        }
    }

    function extendSchemaAndCombineAllOf(
        ns: any,
        schema: any,
        name: string,
        namespace: string,
        shouldCombineAll: boolean = true
    ) {
        const extended = extendDraftSchema(extendWithVariables(schema))
        const dereferenced = dereference(extended, {ns, common: namespaces.common, name})
        return shouldCombineAll ? combineAllOf(dereferenced) : dereferenced
    }

    function inflateSchema(namespace: string, schema: any, name: string) {
        const dereferenced = dereference(schema, {ns: namespaces[namespace], common: namespaces.common, name})
        return combineAllOf(dereferenced)
    }

    const REFERENCE_TYPES = ['ref', 'weakRef', 'refList']

    function isReference(val: any) {
        return _.intersection(REFERENCE_TYPES, val?.pseudoType).length > 0
    }

    function extractRefsFromSchema(namespace: string, schema: any, result: RefInfo[], parentPath: string[]) {
        const subSchemas = schema.allOf || schema.anyOf || schema.oneOf || []
        _.forEach(subSchemas, subSchema => extractRefsFromSchema(namespace, subSchema, result, parentPath))
        if (schema.properties) {
            const {$ref} = schema.properties
            const currentSchema = $ref ? namespaces[namespace][$ref] : schema
            _.forOwn(currentSchema.properties, (val, key) => {
                if (val?.$ref) {
                    val = namespaces[namespace][val.$ref]
                    if (!val) {
                        return
                    }
                }
                if (isReference(val)) {
                    const path = parentPath.concat(key)
                    const isWeakRef = val?.pseudoType.includes('weakRef')
                    const isList = val?.pseudoType.includes('refList')
                    result.push({
                        path,
                        isList,
                        jsonPointer: `/${path.join('/')}`,
                        shouldCollect: val.shouldCollect ?? !isWeakRef,
                        isRefOwner: val.shouldCollect ?? !isWeakRef,
                        shouldValidate: val.shouldValidate ?? !isWeakRef,
                        referencedMap: val.referencedMap ?? namespace,
                        isRelationalSplit: val.isRelationalSplit,
                        refTypes: val.refTypes ?? []
                    })
                } else if (val.type === 'object' && val.properties) {
                    extractRefsFromSchema(namespace, val, result, parentPath.concat(key))
                } else if (val.type === 'array' && val.items) {
                    extractRefsFromSchema(namespace, val.items, result, parentPath.concat(key))
                }
            })
        }
    }

    const combineReferences = (refs: RefInfo[]): RefInfo[] => {
        if (refs.length > 1) {
            const groups = _.groupBy(refs, 'jsonPointer')
            return _.map(groups, group => {
                if (group.length === 1) {
                    return group[0]
                }
                const refTypes = _.flatMap(group, 'refTypes')
                return {
                    ...group[0],
                    refTypes
                }
            })
        }
        return refs
    }

    function extractAndAddReferences(namespace: string, name: string) {
        const ns = namespaces[namespace]
        const schema = ns[name]
        const refs: RefInfo[] = []
        extractRefsFromSchema(namespace, schema, refs, [])
        if (refs.length) {
            const combined = combineReferences(refs)
            addReferences(namespace, name, combined)
        }
    }

    function addReferences(namespace: string, name: string, refs: RefInfo[]) {
        try {
            validateReferences(refs)
        } catch (error) {
            throw new Error(`References validation failed: ${(error as Error).message}`)
        }
        _.setWith(referencesMap, [namespace, name], keyByPath(refs), Object)
    }

    function addDataTypesToExistingNamespace(
        namespace: string,
        schemas: Record<string, SchemaFile>,
        override: boolean = false,
        shouldCombineAll: boolean = true
    ) {
        const ns = namespaces[namespace]
        const id = ns.$id
        removeSchema(id)
        _.forEach(schemas, (schema, name) => {
            const extended = extendSchemaAndCombineAllOf(ns, schema, name, namespace, shouldCombineAll)
            ns[name] = extended
            if (restrictedSchemas) {
                restrictedSchemas[namespace][name] = extended
            }
            if (override) {
                clearAjvFragmentCacheForSchema(namespace, ns[name] as unknown as SchemaFile, name)
            }
            extractAndAddReferences(namespace, name)
        })
        addInitialSchema(ns, id, namespace)
    }

    function registerNamespace(name: string, schemas: SchemaFile) {
        if (!namespaces[name]) {
            const ns = schemas.$id ? schemas : {...schemas, $id: name}
            namespaces[name] = ns
            addInitialSchema(ns, ns.$id, name)
        }
    }

    const isDraftDataSchema = (namespace: string, dataTypeName: string) =>
        _.get(namespaces, [namespace, dataTypeName, 'isDraftSchema'], false)
    const isDraftItem = (dataItem: any) => !!dataItem?._isDraftItem

    function extractReferences(namespace: string, dataTypeName: string, data: any): readonly ResolvedReference[] {
        return referenceUtil.extractReferences(referencesMap, namespace, dataTypeName, data)
    }

    //this function extracts the fields from the data item which are 'reference' fields according to the schema
    function extractReferenceFieldsInfo(namespace: string, dataTypeName: string, data?: any): readonly RefInfo[] {
        const refs = _.get(referencesMap, [namespace, dataTypeName])
        if (data) {
            return _.filter(refs, (refInfo: RefInfo) => _.has(data, refInfo.path))
        }
        return _.values(refs)
    }

    function extractOwnedReferenceFieldsInfo(
        namespace: string,
        dataTypeName: string,
        onlySerializable?: boolean,
        data?: any
    ): Record<string, RefInfo> {
        const refs = _.get(referencesMap, [namespace, dataTypeName])
        return _.pickBy(
            refs,
            refInfo =>
                refInfo.isRefOwner &&
                (!data || _.has(data, refInfo.path)) &&
                (!onlySerializable || !refInfo.dontSerialize)
        )
    }

    const isPermanentDataType = (namespace: string, type: string): boolean =>
        _.get(permanentDataTypes, [namespace, type], false)

    return {
        addDefaultsAndValidate,
        validate: validateNoDefaults,
        validateStrict,
        validateNoDefaults,
        hasNamespace,
        hasSchemaForDataType,
        isPermanentDataType,
        registerNamespace,
        addDataTypesToExistingNamespace,
        isDraftDataSchema,
        isDraftItem,
        extractReferenceFieldsInfo,
        extractOwnedReferenceFieldsInfo,
        extractReferences,
        addReferences,
        removeAdditionalProperties,
        options: Object.freeze(schemaServiceOptions),
        inflateSchema,
        getInflatedSchemaFromCache,
        addInflatedSchemaToCache
    }
}

export {createSchemaCore}
